import React, { useState } from "react";
import styled, { css, useTheme } from "styled-components";
import { Info, X } from "lucide-react";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
  getPanelShadow,
} from "common/NewLook/tokens.ts";
import Text from "common/Typography/Text.tsx";
import Title from "common/Typography/Title.tsx";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import localStorageService from "utils/localStorageService.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

const TradingViewBotsBanner = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isNewLook } = useTheme();

  const [hideBanner, setHideBanner] = useState(
    localStorageService.getParsedValue<number>(BANNER_KEY) || false
  );

  const onCloseClick = () => {
    localStorageService.setValue(BANNER_KEY, 1);
    setHideBanner(1);
  };

  if (isNewLook && !hideBanner) {
    return (
      <Wrapper>
        <NewLookNotice>
          <NewLookIcon>
            <Info size={16} strokeWidth={1.75} />
          </NewLookIcon>
          <NewLookTexts>
            <NewLookTitle>{t("trading_view_banner.title")}</NewLookTitle>
            <NewLookText>
              {t("trading_view_banner.description_1")}
              {!isBottomNavigator &&
                ` ${t("trading_view_banner.description_2")}`}
            </NewLookText>
          </NewLookTexts>
          <NewLookClose
            type="button"
            aria-label={t("buttons.close")}
            onClick={onCloseClick}
          >
            <X size={16} strokeWidth={1.75} />
          </NewLookClose>
        </NewLookNotice>
      </Wrapper>
    );
  }

  const banner = (
    <Wrapper>
      <Background>
        <Content>
          <StyledTitle level={3}>{t("trading_view_banner.title")}</StyledTitle>
          <StyledText>
            {t("trading_view_banner.description_1")}
            {!isBottomNavigator && ` ${t("trading_view_banner.description_2")}`}
          </StyledText>
        </Content>
        <CloseIcon onClick={onCloseClick} />
      </Background>
    </Wrapper>
  );

  return hideBanner ? null : banner;
};

export default TradingViewBotsBanner;

const BANNER_KEY = "tradingview_bots_json_banner";
const Wrapper = styled.div`
  width: 100%;
  padding: 16px 0;
  @media (max-width: ${phoneMaxWidth}) {
    padding: 16px 16px 0;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding: 0 0 16px;
      @media (max-width: ${phoneMaxWidth}) {
        padding: 16px 16px 0;
      }
    `}
`;
// A panel like the others on the page: the notice reads first by its title,
// not by a tinted icon.
const NewLookNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 16px 16px 20px;
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  box-shadow: ${(p) => getPanelShadow(p.theme)};
`;
const NewLookIcon = styled.span`
  flex: none;
  display: inline-flex;
  margin-top: 2px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const NewLookTexts = styled.div`
  flex: 1;
  min-width: 0;
`;
const NewLookTitle = styled.div`
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const NewLookText = styled.div`
  margin-top: 4px;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const NewLookClose = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: -4px 0;
  border: none;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: transparent;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  cursor: pointer;
  &:hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 1px;
  }
`;
const Background = styled.div`
  background: ${(p) => p.theme.colors.lightBlue};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;
const Content = styled.div`
  border-left: 2px solid ${(p) => p.theme.colors.primaryBlue};
  padding: 0 12px;
  max-width: 950px;
`;
const StyledTitle = styled(Title)`
  color: ${(p) => p.theme.colors.primaryBlue};
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  margin-bottom: 4px;
`;
const StyledText = styled(Text)`
  color: ${(p) => p.theme.colors.primaryBlue};
`;
const CloseIcon = styled(CloseOutlined)`
  align-self: flex-start;
  cursor: pointer;
`;
